import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../services/api';

const NexusSyncContext = createContext({
  publishMutation: () => {},
  lastMutation: null,
});

export const NexusSyncProvider = ({ children }) => {
  const [mutations, setMutations] = useState([]);
  const queue = useRef([]);
  const timer = useRef(null);

  const publishMutation = useCallback((mutation) => {
    if (!mutation || !mutation.metadata) {
      return;
    }
    queue.current.push(mutation);
    if (timer.current !== null) return;
    const drain = () => {
      const item = queue.current.shift();
      if (item) setMutations((prev) => [...prev, { ...item, timestamp: Date.now() }].slice(-25));
      // Separate renders: several MCP writes in one response must each refresh their own page domain.
      timer.current = queue.current.length ? setTimeout(drain, 50) : null;
    };
    timer.current = setTimeout(drain, 0);
  }, []);

  useEffect(() => {
    let stopped = false;
    let pollTimer;
    let token;
    let cursor = null;
    const poll = async () => {
      const currentToken = localStorage.getItem('auth_token');
      if (token !== currentToken) {
        token = currentToken;
        cursor = null;
        queue.current = [];
        setMutations([]);
      }
      try {
        if (token) {
          const data = await apiService.get(`/agent-access/events${cursor === null ? '' : `?after=${cursor}`}`);
          if (!stopped && token === localStorage.getItem('auth_token')) {
            (data.items || []).forEach(publishMutation);
            cursor = data.cursor;
            pollTimer = setTimeout(poll, data.has_more ? 50 : 3000);
            return;
          }
        }
      } catch (_) { /* Keep the cursor and recover after a transient network failure. */ }
      if (!stopped) pollTimer = setTimeout(poll, 3000);
    };
    poll();
    return () => {
      stopped = true;
      clearTimeout(pollTimer);
      clearTimeout(timer.current);
      timer.current = null;
      queue.current = [];
    };
  }, [publishMutation]);

  const value = useMemo(
    () => ({
      publishMutation,
      lastMutation: mutations[mutations.length - 1] || null,
    }),
    [mutations, publishMutation]
  );

  return <NexusSyncContext.Provider value={value}>{children}</NexusSyncContext.Provider>;
};

export const useNexusSync = () => useContext(NexusSyncContext);

export const useNexusRefresh = (entities, refresh) => {
  const { lastMutation } = useNexusSync();
  const callback = useRef(refresh);
  callback.current = refresh;
  const filter = entities.join(',');
  useEffect(() => {
    if (lastMutation && filter.split(',').includes(lastMutation.metadata?.entity)) callback.current();
  }, [lastMutation, filter]);
};
